import React, { FunctionComponent, useCallback } from 'react';
import {
  Modal, Input, useModal, Portal, ICON_MAGNIFY,
} from '@doveit/lego';
import AsyncFilterableList from '../../components/async-suggestion-list/AsyncSuggestionList';
import * as styles from './SearchPlaceModal.style';
import { suggestPlace, Place } from '../../../services/place/place';
import mapToSelectableItemProps from '../../utils/place/mapToSelectableItemProps';
import { FormFieldConfig } from '../../types';

export interface SearchPlaceModalProps {
  onPlaceSelected: (place: Place) => void,
  initialValue?: string,
  loading?: boolean,
  field?: FormFieldConfig,
}

const SearchPlaceModal: FunctionComponent<SearchPlaceModalProps> = ({
  onPlaceSelected,
  initialValue = '',
  loading,
  field = {},
}) => {
  const modal = useModal();

  const fetcher = useCallback(async (value: string) => {
    const results = await suggestPlace(value, { size: 20 });

    return results.map(mapToSelectableItemProps);
  }, []);

  const onInternalItemSelected = useCallback(
    (item) => {
      modal.close();
      onPlaceSelected(item.data);
    },
    [modal, onPlaceSelected],
  );

  const onSearchInputRef = useCallback((input: HTMLInputElement | null) => {
    if (!input) {
      return () => undefined;
    }

    const timer = setTimeout(() => input.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Input
        placeholder={field.placeholder || 'Inserisci città, provincia o quartiere'}
        aria-label={field.ariaLabel || 'Pulsante per aprire box ricerca casa'}
        onFocus={modal.open}
        icon={ICON_MAGNIFY}
        data-ref="search-button"
        value={initialValue}
        disabled={loading}
        readOnly
      />
      <Portal>
        <Modal
          open={modal.isOpen}
          onCloseHandler={modal.close}
        >
          <Modal.Close />
          <Modal.Body>
            <AsyncFilterableList
              fetcher={fetcher}
              onItemSelect={onInternalItemSelected}
            >
              <div css={styles.mobileSearchBox}>
                <div css={styles.mobileSearchContent}>
                  <div css={styles.mobileSearchHeader}>
                    <AsyncFilterableList.Input
                      label={field.label || 'Dove cerchi casa?'}
                      placeholder={field.placeholder || 'Inserisci città, provincia o quartiere'}
                      forwardedRef={onSearchInputRef}
                    />
                  </div>
                  <div css={styles.mobileSearchList}>
                    <AsyncFilterableList.Items />
                  </div>
                </div>
              </div>
            </AsyncFilterableList>
          </Modal.Body>
        </Modal>
      </Portal>
    </>
  );
};

export default SearchPlaceModal;
